import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type CreateDeliveryInput,
  useCreateDeliveryApi,
  useDeliveryApi,
  useSendDeliveryApi,
} from "hooks/use-delivery-api";
import { useCreateDeliverySeries } from "hooks/use-delivery-series-api";
import { useSenderEmails } from "hooks/use-sender-emails-api";
import { DeliveryTiming } from "hooks/useEmailTemplatesRestApi";
import { type FC, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  DeliveryForm,
  type DeliveryFormValues,
} from "routes/DeliveryPage/components/DeliveryForm";
import {
  type DeliveryTypeSelection,
  DeliveryTypeSelector,
} from "routes/DeliveryPage/components/DeliveryTypeSelector";
import { fromDateTimeLocalValue } from "utils/relative-time";
import { toastError } from "utils/toast-utils";

/**
 * 配信新規作成ページ.
 *
 * 動線:
 * - 通常: Step 1 (配信タイプ選択) → Step 2 (内容編集)
 * - ?copy-from=&lt;deliveryId&gt; が来た場合: Step 1 をスキップ。 コピー元の series_id を引き継ぐ
 * - ?seriesId=&lt;seriesId&gt; が来た場合: Step 1 をスキップ。 該当シリーズに加入
 * @return {JSX.Element} ページ
 */
export const DeliveryNewPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get("copy-from");
  const querySeriesId = searchParams.get("seriesId");

  const { createDelivery, loading: createLoading } = useCreateDeliveryApi();
  const { sendDelivery, loading: sendLoading } = useSendDeliveryApi();
  const { create: createSeries, loading: creatingSeries } =
    useCreateDeliverySeries();
  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });
  const { data: sourceDelivery, loading: sourceLoading } = useDeliveryApi(
    copyFrom || "",
  );

  // Step 1 をスキップする条件
  // - copy-from が指定された (再配信フロー)
  // - seriesId が指定された (シリーズ詳細から)
  const shouldSkipStep1 = Boolean(copyFrom) || Boolean(querySeriesId);
  const [step, setStep] = useState<"select-type" | "edit">(
    shouldSkipStep1 ? "edit" : "select-type",
  );

  const [selection, setSelection] = useState<DeliveryTypeSelection>(() => {
    if (querySeriesId) {
      return {
        choice: "existing",
        selectedSeriesId: querySeriesId,
        newSeriesName: "",
      };
    }
    return {
      choice: "single",
      selectedSeriesId: null,
      newSeriesName: "",
    };
  });

  const initialValues = useMemo(() => {
    if (!copyFrom || !sourceDelivery) return undefined;
    return {
      distributionGroupId: sourceDelivery.groupId ?? "",
      from: sourceDelivery.fromEmail,
      subject: sourceDelivery.subject,
      body: sourceDelivery.bodyText,
      includeSender: sourceDelivery.includeSender,
      deliveryTiming: DeliveryTiming.Now,
      scheduledAt: "",
    };
  }, [copyFrom, sourceDelivery]);

  /**
   * 提出時のシリーズ ID を解決する. 必要なら新規シリーズを先に作成する.
   * @return {Promise<string | null>} 確定した seriesId (null = 単発)
   */
  const resolveSeriesId = async (): Promise<string | null> => {
    // copy-from の場合: コピー元の series_id を継承
    if (copyFrom && sourceDelivery) {
      return sourceDelivery.seriesId ?? null;
    }
    // 明示的に ?seriesId= で来た場合
    if (querySeriesId) return querySeriesId;
    // Step 1 で選んだ内容を反映
    switch (selection.choice) {
      case "single":
        return null;
      case "existing":
        return selection.selectedSeriesId;
      case "new": {
        const created = await createSeries({
          name: selection.newSeriesName.trim(),
        });
        return created.id;
      }
    }
  };

  const buildCreateInput = async (
    values: DeliveryFormValues,
  ): Promise<CreateDeliveryInput> => {
    const senders = senderEmailsData?.items ?? [];
    const sender = senders.find(s => s.email === values.from);
    const seriesId = await resolveSeriesId();
    return {
      groupId: values.distributionGroupId,
      seriesId: seriesId ?? null,
      fromEmail: values.from,
      fromName: sender?.displayName || undefined,
      subject: values.subject.trim(),
      bodyText: values.body.trim(),
      includeSender: values.includeSender,
      scheduledAt:
        values.deliveryTiming === DeliveryTiming.Scheduled
          ? (fromDateTimeLocalValue(values.scheduledAt) ?? undefined)
          : undefined,
    };
  };

  const handleSaveDraft = async (values: DeliveryFormValues): Promise<void> => {
    try {
      const input = await buildCreateInput(values);
      await createDelivery({ ...input, scheduledAt: undefined });
      navigate("/deliveries");
    } catch {
      toastError("下書き保存に失敗しました");
    }
  };

  const handleSend = async (values: DeliveryFormValues): Promise<void> => {
    try {
      const input = await buildCreateInput(values);
      const delivery = await createDelivery(input);
      if (values.deliveryTiming === DeliveryTiming.Now) {
        await sendDelivery(delivery.id);
      }
      navigate("/deliveries");
    } catch {
      toastError("配信に失敗しました");
    }
  };

  if (copyFrom && sourceLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (step === "select-type") {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center gap-4 mb-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/deliveries">&larr; 戻る</Link>
          </Button>
          <PageTitle>新規メール配信</PageTitle>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          {creatingSeries && (
            <div className="mb-3 text-xs text-base-content/60">
              シリーズを作成中…
            </div>
          )}
          <DeliveryTypeSelector
            value={selection}
            onChange={setSelection}
            onConfirm={() => setStep("edit")}
            onCancel={() => navigate("/deliveries")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/deliveries">&larr; 戻る</Link>
        </Button>
        <PageTitle>新規メール配信</PageTitle>
      </div>

      <DeliveryStepBanner
        copyFrom={copyFrom}
        querySeriesId={querySeriesId}
        selection={selection}
        sourceSeriesName={sourceDelivery?.seriesName ?? null}
        canGoBack={!shouldSkipStep1}
        onBack={() => setStep("select-type")}
      />

      <DeliveryForm
        key={copyFrom || querySeriesId || "new"}
        initialValues={initialValues}
        saveDraftLabel="下書き保存"
        saveLoading={createLoading || creatingSeries}
        sendLoading={sendLoading || creatingSeries}
        onSaveDraft={handleSaveDraft}
        onSend={handleSend}
      />
    </div>
  );
};

type DeliveryStepBannerProps = {
  copyFrom: string | null;
  querySeriesId: string | null;
  selection: DeliveryTypeSelection;
  sourceSeriesName: string | null;
  canGoBack: boolean;
  onBack: () => void;
};

/**
 * Step 2 上部のシリーズコンテキスト表示.
 * @param {DeliveryStepBannerProps} props 状態
 * @return {JSX.Element|null} バナー
 */
const DeliveryStepBanner: FC<DeliveryStepBannerProps> = ({
  copyFrom,
  querySeriesId,
  selection,
  sourceSeriesName,
  canGoBack,
  onBack,
}) => {
  let label: string | null = null;
  if (copyFrom) {
    label = sourceSeriesName
      ? `「${sourceSeriesName}」シリーズの再配信 (直近を雛型)`
      : "再配信 (直近を雛型)";
  } else if (querySeriesId) {
    label = "シリーズに加入する配信を作成";
  } else if (selection.choice === "single") {
    label = "単発配信を作成";
  } else if (selection.choice === "existing") {
    label = "既存シリーズに加入する配信を作成";
  } else if (selection.choice === "new") {
    label = `新規シリーズ「${selection.newSeriesName}」 を起点に配信を作成`;
  }
  if (!label) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-violet-50 border border-violet-200 text-sm">
      <span className="text-violet-900">{label}</span>
      {canGoBack && (
        <button
          type="button"
          className="text-xs text-violet-700 hover:underline"
          onClick={onBack}
        >
          配信タイプを変更
        </button>
      )}
    </div>
  );
};
